import { FC, useState } from "react";
import styled from "@emotion/styled";
import {
  MDSActionMenu,
  MDSActionMenuItem,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSIconKind,
  MDSMenuDropdown,
  MDSMenuDropdownSlotName,
  MDSMenuItemHeader,
  MDSMenuItemVariant,
} from "@/mds";
import { BLOCK_TYPE_LIST, BlockType } from "./blockTypes";

interface BlockInsertionRowProps {
  onInsert: (type: BlockType) => void;
  onAddDraft?: () => void;
  onDropBlock?: () => void;
  hideInsertButton?: boolean;
}

export const BlockInsertionRow: FC<BlockInsertionRowProps> = ({
  onInsert,
  onAddDraft,
  onDropBlock,
  hideInsertButton = false,
}) => {
  const [open, setOpen] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  return (
    <Wrapper
      data-drag-over={isDragOver}
      $open={open}
      $isDragOver={isDragOver}
      onDragOver={event => {
        if (!onDropBlock) return;

        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
        setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={event => {
        if (!onDropBlock) return;

        event.preventDefault();
        setIsDragOver(false);
        onDropBlock();
      }}
    >
      <Line $isDragOver={isDragOver} />
      <ButtonHolder $hidden={hideInsertButton || isDragOver}>
        {onAddDraft ? (
          <MDSIconButton
            size={MDSIconButtonSize.XSmall}
            iconKind={MDSIconKind.Plus}
            variant={MDSIconButtonVariant.OnChrome}
            onClick={onAddDraft}
            rounded
          />
        ) : (
          <MDSMenuDropdown onOpenChange={setOpen}>
            <MDSIconButton
              slot={MDSMenuDropdownSlotName.Trigger}
              size={MDSIconButtonSize.XSmall}
              iconKind={MDSIconKind.Plus}
              variant={MDSIconButtonVariant.OnChrome}
              rounded
            />
            <MDSActionMenu
              aria-label="Block types"
              style={{ minWidth: 240 }}
              slot={MDSMenuDropdownSlotName.Menu}
            >
              <MDSMenuItemHeader label="Add block" />
              {BLOCK_TYPE_LIST.map(meta => (
                <MDSActionMenuItem
                  key={meta.type}
                  id={meta.type}
                  variant={MDSMenuItemVariant.OneLine}
                  label={meta.label}
                  icon={meta.iconKind}
                  onClick={() => onInsert(meta.type)}
                />
              ))}
            </MDSActionMenu>
          </MDSMenuDropdown>
        )}
      </ButtonHolder>
    </Wrapper>
  );
};

const Wrapper = styled.div<{ $open: boolean; $isDragOver: boolean }>`
  position: relative;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ $isDragOver, $open }) => ($isDragOver || $open ? 1 : 0)};
  transition: opacity 0.15s ease;
  &:hover {
    opacity: 1;
  }
`;

const Line = styled.div<{ $isDragOver: boolean }>`
  position: absolute;
  inset: 50% 0 auto 0;
  height: ${({ $isDragOver }) => ($isDragOver ? "2px" : "1px")};
  background: ${({ $isDragOver, theme }) =>
    $isDragOver ? theme.colors.border.emphasis : theme.colors.border.default};
  border-radius: ${({ $isDragOver }) => ($isDragOver ? "999px" : 0)};
`;

const ButtonHolder = styled.div<{ $hidden: boolean }>`
  position: relative;
  background: ${({ theme }) => theme.colors.surface.pane};
  padding: 0 4px;
  display: ${({ $hidden }) => ($hidden ? "none" : "block")};
`;
